import React, { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

const BookingModal = ({
  isOpen,
  onClose,
  onConfirm,
  priceId,
  childrenCount = 0,
  currency = "USD",
  price = 0,
  transferType = "rent",
  additionalOptions = [],
  // v2: выбор обратного трансфера
  isV2 = false,
  searchReturn,
  returnCalendar,
  passengers,
  ReturnCard,
}) => {
  const [pickupSign, setPickupSign] = useState("")
  const [additionalAddress, setAdditionalAddress] = useState("")
  const [childrenAges, setChildrenAges] = useState(Array(childrenCount).fill(""))
  const [comment, setComment] = useState("")

  const [returnOpen, setReturnOpen] = useState(false)
  const [returnDates, setReturnDates] = useState(returnCalendar?.dates?.return ?? [])
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [returnGroups, setReturnGroups] = useState(null)
  const [returnLoading, setReturnLoading] = useState(false)
  const [returnError, setReturnError] = useState(null)
  const [returnPriceId, setReturnPriceId] = useState(null)

  // overflow включаем сразу при открытии календаря, а выключаем с задержкой,
  // пока попап календаря доигрывает exit-анимацию — иначе мелькает скролл
  const [overflowVisible, setOverflowVisible] = useState(false)
  useEffect(() => {
    if (calendarOpen) {
      setOverflowVisible(true)
      return
    }
    const id = setTimeout(() => setOverflowVisible(false), 350)
    return () => clearTimeout(id)
  }, [calendarOpen])
  const showOverflow = calendarOpen || overflowVisible

  const showPickup = additionalOptions.includes("pickup")
  const showAdditionalAddress = additionalOptions.includes("additional_address")
  const showComment = additionalOptions.includes("comment")
  const hasPickupSection = showPickup || showAdditionalAddress

  const ReturnDateCard = returnCalendar?.DateCard
  const canAddReturn =
    isV2 && typeof searchReturn === "function" && !!ReturnCard && !!ReturnDateCard

  // сброс состояния возврата при закрытии модалки
  useEffect(() => {
    if (isOpen) return
    setReturnOpen(false)
    setReturnDates(returnCalendar?.dates?.return ?? [])
    setCalendarOpen(false)
    setReturnGroups(null)
    setReturnLoading(false)
    setReturnError(null)
    setReturnPriceId(null)
  }, [isOpen, returnCalendar?.dates?.return])

  const selectedReturn = useMemo(() => {
    if (!returnPriceId) return null
    for (const group of returnGroups ?? []) {
      const p = (group.prices ?? []).find((x) => x.id === returnPriceId)
      if (p) return { group, price: p }
    }
    return null
  }, [returnPriceId, returnGroups])

  const returnAmount = selectedReturn
    ? selectedReturn.group.transfer_type === "group"
      ? selectedReturn.price.total
      : selectedReturn.price.adult
    : 0
  const total = price + returnAmount

  const runReturnSearch = async (date) => {
    if (!date) return
    setReturnLoading(true)
    setReturnError(null)
    setReturnPriceId(null)
    try {
      const transfers = await searchReturn(date)
      setReturnGroups(transfers ?? [])
    } catch (e) {
      setReturnGroups([])
      setReturnError("Search failed. Try again.")
    } finally {
      setReturnLoading(false)
    }
  }

  const openReturn = () => {
    setReturnOpen(true)
    if (returnDates[0]) runReturnSearch(returnDates[0])
    else setCalendarOpen(true)
  }

  const closeReturn = () => {
    setReturnOpen(false)
    setCalendarOpen(false)
    setReturnPriceId(null)
    setReturnGroups(null)
    setReturnError(null)
  }

  // выбор даты в календаре: сразу запускаем поиск
  const handleReturnDatesChange = ({ to }) => {
    const next = to ?? []
    setReturnDates(next)
    if (next[0]) {
      setCalendarOpen(false)
      runReturnSearch(next[0])
    } else {
      setReturnGroups(null)
      setReturnPriceId(null)
    }
  }

  const handleConfirm = () => {
    onConfirm({
      transfer_price_id: priceId,
      ...(showPickup ? { pickup_sign: pickupSign } : {}),
      ...(showAdditionalAddress ? { additional_address: additionalAddress } : {}),
      children_ages: childrenAges,
      ...(showComment ? { comment } : {}),
      ...(isV2 && selectedReturn
        ? { return_price_id: selectedReturn.price.id, return_from: returnDates[0] }
        : {}),
    })
  }

  // v2 с возвратом: сразу широкая модалка (под календарь на 2 месяца), без скачков ширины.
  // v1: canAddReturn === false -> остаётся узкая.
  const wide = canAddReturn

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/50" onClick={onClose} />

          <motion.div
            className={`relative bg-white rounded-t-2xl sm:rounded-xl border border-gray-100 shadow-sm w-full ${
              showOverflow ? "overflow-visible" : "overflow-hidden"
            } ${wide ? "sm:max-w-3xl" : "sm:max-w-lg"}`}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* header */}
            <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-app-base-strong leading-tight">
                  Complete your booking
                </h3>
                <p className="text-[10px] font-medium text-app-content-faint uppercase tracking-wider mt-1">
                  {transferType === "group" ? "Group" : "Individual"} transfer
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-app-content-faint hover:text-app-content transition-colors mt-0.5"
              >
                <XIcon />
              </button>
            </div>

            {/* body: пока открыт календарь, снимаем overflow, чтобы попап не обрезался */}
            <div
              className={`px-5 py-4 flex flex-col gap-5 max-h-[70vh] ${
                showOverflow ? "overflow-visible" : "overflow-y-auto"
              }`}
            >
              {/* return transfer (v2) */}
              {canAddReturn && (
                <section>
                  {!returnOpen ? (
                    <button
                      onClick={openReturn}
                      className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-app-base-strong border border-dashed border-app-base-strong/40 rounded-xl hover:bg-app-base-strong/5 transition-colors"
                    >
                      <PlusIcon />
                      Add return transfer
                    </button>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider">
                          Return transfer
                        </p>
                        <button
                          onClick={closeReturn}
                          className="text-xs text-app-content-faint hover:text-app-content transition-colors"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="flex flex-col gap-3">
                        {/* тот же DateCard, что в Panel; depart берём из основной формы */}
                        <ReturnDateCard
                          dates={{ depart: returnCalendar.dates.depart, return: returnDates }}
                          onDatesChange={handleReturnDatesChange}
                          handleModal={() => setCalendarOpen((v) => !v)}
                          isOpenCalendar={calendarOpen}
                          availableDepart={returnCalendar.availableDepart}
                          availableRetur={returnCalendar.availableRetur}
                          departPriceData={returnCalendar.departPriceData}
                          returPriceData={returnCalendar.returPriceData}
                          onCalendarMonthChange={returnCalendar.onCalendarMonthChange}
                        />

                        {selectedReturn ? (
                          <div className="flex items-center justify-between gap-3 border border-emerald-100 bg-emerald-50/50 rounded-xl px-3 py-2.5">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-app-content leading-tight truncate">
                                {selectedReturn.price.transport_type || selectedReturn.price.name}
                              </p>
                              <p className="text-xs text-app-content-faint mt-0.5 truncate">
                                {selectedReturn.group.name}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="text-sm font-semibold text-app-base-strong">
                                {selectedReturn.price.currency || currency}{" "}
                                {returnAmount.toFixed(0)}
                              </span>
                              <button
                                onClick={() => setReturnPriceId(null)}
                                className="text-xs text-app-base-strong hover:text-app-base transition-colors"
                              >
                                Change
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {returnLoading && (
                              <p className="text-xs text-app-content-faint py-4 text-center">
                                Searching…
                              </p>
                            )}
                            {returnError && (
                              <p className="text-xs text-red-500 py-2 text-center">{returnError}</p>
                            )}
                            {!returnLoading && returnGroups && returnGroups.length === 0 && (
                              <p className="text-xs text-app-content-faint py-4 text-center">
                                No return transfers found
                              </p>
                            )}
                            {!returnLoading && returnGroups && returnGroups.length > 0 && (
                              <div className="flex flex-col gap-3">
                                {returnGroups.map((group, i) => (
                                  <ReturnCard
                                    key={`${group.transfer_type}-${group.transport_type_code}-${group.name}`}
                                    data={group}
                                    index={i}
                                    passengers={passengers ?? { children: 0 }}
                                    priceFormVersion="v2"
                                    selectMode
                                    onSelect={setReturnPriceId}
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* pickup */}
              {hasPickupSection && (
                <section>
                  <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider mb-3">
                    Pickup details
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {showPickup && (
                      <Field label="Pick up sign">
                        <input
                          type="text"
                          placeholder="Name shown on driver's board"
                          value={pickupSign}
                          onChange={(e) => setPickupSign(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong placeholder:text-app-content-faint outline-none focus:border-app-base-strong/40 transition-colors"
                        />
                      </Field>
                    )}
                    {showAdditionalAddress && (
                      <Field label="Additional address">
                        <input
                          type="text"
                          placeholder="Extra drop-off or pickup point"
                          value={additionalAddress}
                          onChange={(e) => setAdditionalAddress(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong placeholder:text-app-content-faint outline-none focus:border-app-base-strong/40 transition-colors"
                        />
                      </Field>
                    )}
                  </div>
                </section>
              )}

              {/* children ages — только если есть дети */}
              {childrenCount > 0 && (
                <section>
                  <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider mb-3">
                    Children ages
                  </p>
                  <div className="flex flex-col gap-2">
                    {childrenAges.map((age, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-app-content-faint w-14 shrink-0">
                          Child {i + 1}
                        </span>
                        <div className="flex-1 relative">
                          <select
                            value={age}
                            onChange={(e) => {
                              const updated = [...childrenAges]
                              updated[i] = e.target.value
                              setChildrenAges(updated)
                            }}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong outline-none focus:border-app-base-strong/40 appearance-none bg-white transition-colors"
                          >
                            <option value="">Select age</option>
                            {Array.from({ length: 12 }, (_, j) => (
                              <option key={j + 1} value={j + 1}>
                                {j + 1} {j === 0 ? "year" : "years"}
                              </option>
                            ))}
                          </select>
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <ChevronDown size={14} />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* comment */}
              {showComment && (
                <section>
                  <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider mb-3">
                    Comment
                  </p>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Flight number, special requests, luggage info…"
                    rows={3}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong placeholder:text-app-content-faint outline-none focus:border-app-base-strong/40 resize-none transition-colors"
                  />
                </section>
              )}

              {/* summary */}
              <div className="bg-gray-50/70 border border-gray-100 rounded-xl px-4 py-3">
                <div className="flex justify-between text-xs text-app-content-faint mb-2">
                  <span>Transfer</span>
                  <span>
                    {currency} {price.toFixed(0)}
                  </span>
                </div>
                {selectedReturn && (
                  <div className="flex justify-between text-xs text-app-content-faint mb-2">
                    <span>Return transfer</span>
                    <span>
                      {currency} {returnAmount.toFixed(0)}
                    </span>
                  </div>
                )}
                {transferType === "group" && (
                  <div className="flex justify-between text-xs text-app-content-faint mb-2">
                    <span>Pricing</span>
                    <span>per group</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2.5 flex justify-between items-center">
                  <span className="text-sm font-semibold text-app-base-strong">Total</span>
                  <span className="text-xl font-bold text-app-base-strong tracking-tight">
                    {currency} {total.toFixed(0)}
                  </span>
                </div>
              </div>
            </div>

            {/* footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2.5">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 text-sm font-medium text-app-content border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                onClick={handleConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="flex-[2] py-2.5 text-sm font-semibold text-white bg-app-base-strong hover:bg-app-base rounded-xl transition-colors shadow-sm shadow-app-base-strong/25"
              >
                Book now
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs text-app-content-faint font-medium">{label}</span>
    {children}
  </label>
)

const XIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const PlusIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
  </svg>
)

const ChevronDown = ({ size = 16 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

export default BookingModal
